import React, { useEffect, useRef, useState } from 'react';
import {
  User,
  reload,
  sendEmailVerification,
  updateEmail
} from 'firebase/auth';

type Props = {
  user: User; // the signed-in Firebase user
  companyLogoSrc: string; // same logo you use on login
  onVerified: () => void; // called once emailVerified === true
  allowEditEmail?: boolean; // optional: let user correct their email here
};

const EmailVerification = ({
  user,
  companyLogoSrc,
  onVerified,
  allowEditEmail = true
}: Props) => {
  const [statusMsg, setStatusMsg] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState(user.email || '');
  const [resendCooldown, setResendCooldown] = useState<number>(0);
  const cooldownRef = useRef<number | null>(null);

  // countdown for resend button
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown(s => s - 1), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  // If the user somehow becomes verified while this page is open
  useEffect(() => {
    if (user.emailVerified) onVerified();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.emailVerified]);

  async function sendVerification() {
    setErrorMsg('');
    setStatusMsg('');
    setBusy(true);
    try {
      if (!user.email) {
        setErrorMsg('No email address on this account.');
        return;
      }
      await sendEmailVerification(user);
      setStatusMsg(`Verification email sent to ${user.email}.`);
      setResendCooldown(30); // small cooldown to prevent spam
    } catch (e) {
      setErrorMsg(e?.message || 'Failed to send verification email.');
    } finally {
      setBusy(false);
    }
  }

  async function refreshAndContinue() {
    setErrorMsg('');
    setStatusMsg('');
    setBusy(true);
    try {
      await reload(user);
      if (user.emailVerified) {
        onVerified();
      } else {
        setStatusMsg(
          'Still not verified. Please click the link in your email, then try again.'
        );
      }
    } catch (e) {
      setErrorMsg(e?.message || 'Could not refresh your verification status.');
    } finally {
      setBusy(false);
    }
  }

  async function saveEmailUpdate() {
    if (!allowEditEmail) return;
    setErrorMsg('');
    setStatusMsg('');
    setBusy(true);
    try {
      if (!email || email === user.email) {
        setStatusMsg('No changes to save.');
      } else {
        await updateEmail(user, email);
        setStatusMsg(
          `Email updated to ${email}. You can now send a verification email.`
        );
      }
    } catch (e) {
      // For security-sensitive operations, Firebase may require recent login
      setErrorMsg(
        e?.message || 'Could not update email. You might need to sign in again.'
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="min-h-full flex flex-col justify-center py-12 sm:px-6 lg:px-8"
      data-testid="verify-email">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <img
          className="mx-auto h-12 w-auto mb-6"
          src={companyLogoSrc}
          alt="Company Logo"
        />
        <h2 className="mt-8 text-center text-3xl font-extrabold text-gray-900">
          Verify your email
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Before you can log in, we need to verify your email address.
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {/* Status / errors */}
          {errorMsg && (
            <div className="text-red-700 mb-3" data-testid="verifyError">
              {errorMsg}
            </div>
          )}
          {statusMsg && (
            <div className="text-green-700 mb-3" data-testid="verifyStatus">
              {statusMsg}
            </div>
          )}

          {/* Email display / edit */}
          <div className="space-y-2 mb-6">
            <label className="block text-sm font-medium text-gray-700">
              Email address
            </label>
            <div className="mt-1">
              {allowEditEmail ? (
                <div className="flex gap-2">
                  <input
                    data-testid="verifyEmailInput"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                  <button
                    type="button"
                    onClick={saveEmailUpdate}
                    className="px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                    disabled={busy}
                    data-testid="saveEmailButton">
                    Save
                  </button>
                </div>
              ) : (
                <div
                  data-testid="verifyEmailText"
                  className="px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-900">
                  {user.email || '—'}
                </div>
              )}
              <p className="text-xs text-gray-500 mt-1">
                We’ll send the verification link to this address.
              </p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="space-y-4">
            <button
              data-testid="sendVerificationButton"
              onClick={sendVerification}
              disabled={busy || resendCooldown > 0 || !email}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-button-text bg-button-bg hover:bg-button-bg-hover hover:text-button-text-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-60"
              title={
                resendCooldown > 0
                  ? `Please wait ${resendCooldown}s before resending`
                  : undefined
              }>
              {resendCooldown > 0
                ? `Resend in ${resendCooldown}s`
                : busy
                ? 'Working…'
                : 'Send verification email'}
            </button>

            <button
              data-testid="continueAfterVerifyButton"
              onClick={refreshAndContinue}
              disabled={busy}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500">
              I’ve verified — Continue
            </button>

            <p className="text-xs text-gray-500 text-center">
              Didn’t get it? Check spam, or wait a moment and resend.
            </p>
          </div>

          {/* Optional: version/footer area to mirror login */}
          <div className="mt-6 text-center text-xs text-gray-400">
            {/* <Version /> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;
